import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ChevronLeft, Loader2, Activity, Stethoscope, HeartHandshake, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, value }) =>
  value ? (
    <div className="grid grid-cols-1 gap-0.5 py-1.5 sm:grid-cols-[200px_1fr]">
      <span className="text-sm font-semibold text-gray-600">{label}</span>
      <span className="text-sm text-gray-900">{value}</span>
    </div>
  ) : null;

const H = ({ children }) => (
  <h2 className="mb-2 mt-6 border-b border-gray-300 pb-1 font-heading text-lg font-semibold text-gray-900">
    {children}
  </h2>
);

export default function SummaryPage() {
  const { role, caseId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [view, setView] = useState("clinical"); // clinical | plain
  const [gen, setGen] = useState(false);

  useEffect(() => {
    api.getCase(caseId).then(setC).catch(() => navigate(`/${role}/cases`));
  }, [caseId]); // eslint-disable-line

  if (!c) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const fd = c.final_diagnosis || {};
  const cp = c.care_plan || {};
  const pg = cp.prognosis || {};
  const fu = c.follow_up || {};
  const ps = c.patient_summary || {};

  const generatePlain = async () => {
    setGen(true);
    try {
      setC(await api.patientSummary(c.id));
    } catch (e) {
      toast.error("Could not generate the plain-language summary. Retry.");
    } finally {
      setGen(false);
    }
  };

  const ToggleBtn = ({ id, label, icon: Icon }) => (
    <button
      data-testid={`summary-toggle-${id}`}
      onClick={() => setView(id)}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        view === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Toolbar (hidden when printing) */}
      <div className="no-print sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background px-6 py-3">
        <button
          data-testid="summary-back"
          onClick={() => navigate(`/${role}/cases/${caseId}`)}
          className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Back to case
        </button>
        <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
          <ToggleBtn id="clinical" label="Clinical" icon={Stethoscope} />
          <ToggleBtn id="plain" label="Plain language" icon={HeartHandshake} />
        </div>
        <Button data-testid="print-btn" onClick={() => window.print()} className="gap-2">
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </Button>
      </div>

      {view === "plain" ? (
        <PlainSummary c={c} ps={ps} gen={gen} onGenerate={generatePlain} />
      ) : (
      /* Printable clinical sheet */
      <div className="mx-auto max-w-3xl px-8 py-8 text-gray-900" data-testid="summary-sheet">
        <div className="mb-6 flex items-center justify-between border-b-2 border-primary pb-3">
          <div className="flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            <div>
              <p className="font-heading text-xl font-semibold text-primary">OncoDx Clinic</p>
              <p className="text-xs text-gray-500">Case Summary</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">{new Date(c.updated_at).toLocaleString()}</p>
        </div>

        <H>Patient</H>
        <Row label="Name" value={c.patient?.name} />
        <Row label="Age / Sex" value={[c.patient?.age && `${c.patient.age} yrs`, c.patient?.sex].filter(Boolean).join(" · ")} />

        <H>History</H>
        <Row label="Chief complaint" value={c.chief_complaint} />
        <Row label="Presenting illness" value={c.hpi} />
        <Row label="Past history" value={c.past_history} />
        <Row label="Medications" value={c.medications} />
        <Row label="Family history" value={c.family_history} />
        <Row label="Social history" value={c.social_history} />

        {c.answers?.some((a) => a.answer) && (
          <>
            <H>Clarifying Questions</H>
            <ul className="flex flex-col gap-2 text-sm">
              {c.answers.filter((a) => a.answer).map((a, i) => (
                <li key={i}><span className="font-semibold">{a.question}</span><br />{a.answer}</li>
              ))}
            </ul>
          </>
        )}

        {c.provisional_diagnoses?.length > 0 && (
          <>
            <H>Provisional Differential Diagnosis</H>
            {c.impression && <p className="mb-2 text-sm italic text-gray-600">{c.impression}</p>}
            <ul className="flex flex-col gap-1 text-sm">
              {c.provisional_diagnoses.map((d, i) => (
                <li key={i}>• <span className="font-semibold">{d.name}</span> ({d.probability}%) — {d.rationale}</li>
              ))}
            </ul>
          </>
        )}

        {c.investigations?.length > 0 && (
          <>
            <H>Investigations & Results</H>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-gray-300 text-left">
                  <th className="py-1 pr-3 font-semibold">Investigation</th>
                  <th className="py-1 font-semibold">Result</th>
                </tr>
              </thead>
              <tbody>
                {c.investigations.map((inv, i) => (
                  <tr key={i} className="border-b border-gray-100 align-top">
                    <td className="py-1 pr-3">{inv.name}</td>
                    <td className="py-1">{c.investigation_results?.[i]?.result || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {fd.name && (
          <>
            <H>Final Diagnosis</H>
            <p className="text-sm font-semibold text-primary">{fd.name}</p>
            {fd.stage && fd.stage !== "N/A" && <p className="text-sm"><b>Stage:</b> {fd.stage}</p>}
            {fd.confidence && <p className="text-sm"><b>Confidence:</b> {fd.confidence}</p>}
            {fd.rationale && <p className="mt-1 text-sm text-gray-700">{fd.rationale}</p>}
          </>
        )}

        {cp.treatments?.length > 0 && (
          <>
            <H>Care Plan & Treatment</H>
            {cp.goals && <p className="mb-2 text-sm"><b>Goals:</b> {cp.goals}</p>}
            <ul className="flex flex-col gap-2 text-sm">
              {cp.treatments.map((t, i) => (
                <li key={i}>• <span className="font-semibold">{t.modality}:</span> {t.detail}{t.evidence ? ` (Evidence: ${t.evidence})` : ""}</li>
              ))}
            </ul>
          </>
        )}

        {(pg.outlook || pg.five_year_survival) && (
          <>
            <H>Prognosis</H>
            {pg.outlook && <p className="text-sm">{pg.outlook}</p>}
            {pg.five_year_survival && <p className="text-sm"><b>5-year outlook:</b> {pg.five_year_survival}</p>}
            {pg.key_factors?.length > 0 && (
              <ul className="mt-1 list-inside list-disc text-sm text-gray-700">
                {pg.key_factors.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            )}
          </>
        )}

        {(fu.schedule?.length > 0 || fu.monitoring_parameters?.length > 0) && (
          <>
            <H>Follow-up & Monitoring</H>
            {fu.monitoring_parameters?.length > 0 && (
              <ul className="mb-2 flex flex-col gap-1 text-sm">
                {fu.monitoring_parameters.map((p, i) => (
                  <li key={i}>• <b>{p.parameter}</b> — {p.frequency} ({p.target})</li>
                ))}
              </ul>
            )}
            {fu.schedule?.length > 0 && (
              <ul className="flex flex-col gap-1 text-sm">
                {fu.schedule.map((s, i) => <li key={i}>• <b>{s.timepoint}:</b> {s.action}</li>)}
              </ul>
            )}
            {fu.red_flags?.length > 0 && (
              <p className="mt-2 text-sm"><b>Return urgently if:</b> {fu.red_flags.join("; ")}</p>
            )}
            {fu.disposition_criteria && (
              <p className="mt-2 text-sm"><b>Final disposal / remission:</b> {fu.disposition_criteria}</p>
            )}
          </>
        )}

        <p className="mt-8 border-t border-gray-300 pt-3 text-xs text-gray-500">
          Demo / educational tool only — not a certified medical device. Evidence modelled on DeVita,
          Hellman &amp; Rosenberg's Cancer: Principles &amp; Practice of Oncology. Always consult a licensed physician.
        </p>
      </div>
      )}
    </div>
  );
}

const PlainBlock = ({ title, value }) =>
  value ? (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-5">
      <h3 className="font-heading text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 text-[15px] leading-relaxed text-gray-800">{value}</p>
    </div>
  ) : null;

function PlainSummary({ c, ps, gen, onGenerate }) {
  const hasContent = ps && Object.values(ps).some((v) => (v || "").toString().trim());
  if (!hasContent) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center" data-testid="plain-summary-empty">
        <HeartHandshake className="mx-auto h-12 w-12 text-primary" strokeWidth={1.5} />
        <h2 className="mt-4 font-heading text-2xl font-semibold text-gray-900">
          A summary written for you
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
          Get a warm, jargon-free explanation of your diagnosis, treatment plan and what to expect —
          written in plain, everyday language.
        </p>
        <Button data-testid="generate-plain-btn" onClick={onGenerate} disabled={gen} className="mt-6 gap-2">
          {gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {gen ? "Writing your summary…" : "Create my plain-language summary"}
        </Button>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-2xl px-8 py-8" data-testid="plain-summary-sheet">
      <div className="mb-6 flex items-center justify-between gap-3 border-b-2 border-primary pb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-6 w-6 text-primary" />
          <div>
            <p className="font-heading text-xl font-semibold text-primary">Your Care Summary</p>
            <p className="text-xs text-gray-500">For {c.patient?.name}, in plain language</p>
          </div>
        </div>
        <Button
          variant="outline"
          data-testid="share-link-btn"
          className="no-print gap-2"
          onClick={() => {
            const link = `${window.location.origin}/share/${c.id}`;
            navigator.clipboard?.writeText(link).then(
              () => toast.success("Shareable link copied to clipboard"),
              () => toast.message(link)
            );
          }}
        >
          <Share2 className="h-4 w-4" /> Share
        </Button>
      </div>
      <div className="flex flex-col gap-4">
        <PlainBlock title="What's going on" value={ps.overview} />
        <PlainBlock title="What your tests showed" value={ps.what_tests_showed} />
        <PlainBlock title="Your diagnosis" value={ps.your_diagnosis} />
        <PlainBlock title="Your treatment plan" value={ps.your_treatment} />
        <PlainBlock title="What to watch for" value={ps.what_to_watch} />
        <PlainBlock title="Your follow-up plan" value={ps.your_followup} />
      </div>
      <div className="no-print mt-6">
        <Button variant="outline" onClick={onGenerate} disabled={gen} className="gap-2">
          {gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Regenerate
        </Button>
      </div>
      <p className="mt-8 border-t border-gray-300 pt-3 text-xs text-gray-500">
        Demo / educational tool only — not a certified medical device. Always consult your doctor.
      </p>
    </div>
  );
}
